/**
 * Load PlanNotebook state from the orchestrator for sidebar hydration.
 * SSE remains authoritative during streaming; this fills gaps after refresh.
 */

import type {
  OrchestratedStatusDict,
  PlanningStrategy,
  SubtaskState,
} from '../types/planning';

const ORCHESTRATOR_URL =
  process.env.REACT_APP_ORCHESTRATOR_URL ||
  process.env.REACT_APP_AGENT_URL ||
  'http://localhost:8090';

export interface PlanStatusRestPayload {
  strategy: string;
  name?: string;
  state?: string;
  subtasks: Array<{ name: string; state: string; outcome?: string | null }>;
}

export interface PlanStatusRestResponse {
  plan_status: PlanStatusRestPayload | null;
  planning_enabled: boolean;
}

function mapRestPayloadToStatus(ps: PlanStatusRestPayload): OrchestratedStatusDict {
  const mapState = (s: string): SubtaskState => {
    const m: Record<string, SubtaskState> = {
      done: 'completed',
      completed: 'completed',
      in_progress: 'in_progress',
      pending: 'pending',
      abandoned: 'abandoned',
      failed: 'abandoned',
    };
    return m[s] ?? 'pending';
  };

  let currentIdx = ps.subtasks.findIndex((st) => st.state === 'in_progress');
  if (currentIdx < 0) {
    currentIdx = ps.subtasks.findIndex((st) => st.state === 'pending');
  }

  return {
    strategy: 'orchestrated',
    original_task: ps.name || 'Plan',
    subtasks: ps.subtasks.map((st, idx) => ({
      index: idx,
      name: st.name,
      description: st.name,
      expected_output: '',
      state: mapState(st.state),
      result: st.outcome ?? undefined,
      is_current: currentIdx >= 0 && idx === currentIdx,
    })),
  };
}

/**
 * Returns strategy + status for usePlanStatus, or null if no plan / disabled.
 */
export async function fetchPlanStatusForSession(
  sessionId: string,
  userId: string,
  getToken: () => string | null,
): Promise<{ strategy: PlanningStrategy; status: OrchestratedStatusDict } | null> {
  const url = new URL(
    `${ORCHESTRATOR_URL}/plan-status/${encodeURIComponent(sessionId)}`,
  );
  url.searchParams.set('user_id', userId);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(url.toString(), { headers });
  if (!res.ok) {
    return null;
  }

  const body = (await res.json()) as PlanStatusRestResponse;
  if (!body.planning_enabled || !body.plan_status) {
    return null;
  }

  const status = mapRestPayloadToStatus(body.plan_status);
  const strategy: PlanningStrategy = 'notebook';
  return { strategy, status };
}
